import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const [marketingSpend, setMarketingSpend] = useState(2500);
  const [selectedProblem, setSelectedProblem] = useState(0);

  const problems = [
    {
      title: "Expensive Marketing That Doesn\'t Work",
      description: "Spending thousands on ads that bring tire-kickers and price shoppers",
      icon: "DollarSign",
      stats: "$2,500-$5,000/month average marketing spend"
    },
    {
      title: "Chasing Payments for Months",
      description: "Completed work but waiting 30-90 days to get paid, hurting cash flow",
      icon: "Clock",
      stats: "Average 45-day payment delay"
    },
    {
      title: "Low-Quality Customers",
      description: "Dealing with customers who haggle, complain, and leave bad reviews",
      icon: "ThumbsDown",
      stats: "60% of leads are price shoppers"
    }
  ];

  const marketingCosts = [
    { platform: "Google Ads", monthly: 1200, leads: 15, quality: "Low" },
    { platform: "Facebook Ads", monthly: 800, leads: 25, quality: "Very Low" },
    { platform: "Home Advisor", monthly: 600, leads: 12, quality: "Low" },
    { platform: "Angie\'s List", monthly: 400, leads: 8, quality: "Medium" },
    { platform: "Yellow Pages", monthly: 300, leads: 3, quality: "Low" }
  ];

  const paymentDelays = [
    { type: "Residential", avgDelay: 30, impact: "Moderate" },
    { type: "Commercial", avgDelay: 60, impact: "High" },
    { type: "Insurance Claims", avgDelay: 90, impact: "Severe" },
    { type: "Property Management", avgDelay: 45, impact: "High" }
  ];

  const annualWaste = marketingSpend * 12;
  const potentialSavings = annualWaste * 0.8; // 80% savings with ProConnect

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Stop Losing Money on Marketing & Payment Delays
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Tampa Bay contractors waste thousands every month on marketing that doesn't work 
            and chase payments that should be guaranteed.
          </p>
        </div>

        {/* Problem Selector */}
        <div className="mb-12">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {problems?.map((problem, index) => (
              <button
                key={index}
                onClick={() => setSelectedProblem(index)}
                className={`p-6 rounded-lg border-2 text-left transition-all duration-200 ${
                  selectedProblem === index
                    ? 'border-primary bg-primary/5 shadow-lg'
                    : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  selectedProblem === index ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                }`}>
                  <Icon name={problem?.icon} size={24} />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{problem?.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{problem?.description}</p>
                <div className="text-xs font-medium text-primary">{problem?.stats}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Split Screen Comparison */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Marketing Expense Calculator */}
          <div className="bg-card rounded-xl p-8 border border-border shadow-testimonial">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center mr-3">
                <Icon name="TrendingDown" size={20} color="white" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Your Marketing Waste</h3>
            </div>

            {/* Marketing Spend Slider */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Monthly Marketing Spend
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="500"
                  max="8000"
                  step="100"
                  value={marketingSpend}
                  onChange={(e) => setMarketingSpend(Number(e?.target?.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>$500</span>
                  <span className="font-bold text-primary">${marketingSpend?.toLocaleString()}</span>
                  <span>$8,000</span>
                </div>
              </div>
            </div>

            {/* Marketing Breakdown */}
            <div className="space-y-3 mb-6">
              {marketingCosts?.map((cost, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <div>
                    <div className="font-medium text-foreground">{cost?.platform}</div>
                    <div className="text-sm text-muted-foreground">{cost?.leads} leads/month</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-foreground">${cost?.monthly}</div>
                    <div className={`text-xs px-2 py-1 rounded ${
                      cost?.quality === 'Very Low' ? 'bg-error/20 text-error' :
                      cost?.quality === 'Low'? 'bg-warning/20 text-warning' : 'bg-accent/20 text-accent'
                    }`}>
                      {cost?.quality}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Annual Waste Calculation */}
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-error mb-1">
                  ${annualWaste?.toLocaleString()}
                </div>
                <div className="text-sm text-error">Wasted annually on ineffective marketing</div>
              </div>
            </div>
          </div>

          {/* Right Side - Payment Delay Timeline */}
          <div className="bg-card rounded-xl p-8 border border-border shadow-testimonial">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-warning rounded-lg flex items-center justify-center mr-3">
                <Icon name="Clock" size={20} color="white" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Payment Delays Impact</h3>
            </div>

            {/* Payment Timeline */}
            <div className="space-y-4 mb-6">
              {paymentDelays?.map((delay, index) => (
                <div key={index} className="relative">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium text-foreground">{delay?.type}</div>
                      <div className="text-sm text-muted-foreground">Average delay: {delay?.avgDelay} days</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      delay?.impact === 'Severe' ? 'bg-error/20 text-error' :
                      delay?.impact === 'High'? 'bg-warning/20 text-warning' : 'bg-accent/20 text-accent'
                    }`}>
                      {delay?.impact} Impact
                    </div>
                  </div>
                  
                  {/* Progress bar showing delay */}
                  <div className="mt-2 w-full bg-muted rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full ${
                        delay?.avgDelay >= 90 ? 'bg-error' :
                        delay?.avgDelay >= 60 ? 'bg-warning': 'bg-accent'
                      }`}
                      style={{ width: `${(delay?.avgDelay / 90) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Cash Flow Impact */}
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-warning mb-1">
                  45 Days
                </div>
                <div className="text-sm text-warning">Average time to get paid</div>
                <div className="text-xs text-muted-foreground mt-2">
                  Hurting your cash flow and business growth
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-primary/5 border border-primary/20 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            What if you could eliminate both problems?
          </h3>
          <p className="text-lg text-muted-foreground mb-6">
            Save ${potentialSavings?.toLocaleString()} annually on marketing and get paid within 24 hours
          </p>
          <div className="flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center text-accent">
              <Icon name="Check" size={16} className="mr-2" />
              Zero marketing costs
            </div>
            <div className="flex items-center text-accent">
              <Icon name="Check" size={16} className="mr-2" />
              Guaranteed payments
            </div>
            <div className="flex items-center text-accent">
              <Icon name="Check" size={16} className="mr-2" />
              Premium customers only
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;